"use client";

import { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { ChevronDown } from "lucide-react";

import { useLanguage } from "@/contexts/LanguageContext";

export default function LanguageSelector({ className = "" }) {
  const { locale, setLocale, languages } = useLanguage();
  const [open, setOpen] = useState(false);
  const buttonRef = useRef(null);
  const listRef = useRef(null);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
  const current = languages.find((item) => item.code === locale) ?? languages[0];

  const close = useCallback(() => setOpen(false), []);

  /* Position the portal menu beneath the trigger button */
  useEffect(() => {
    if (!open || !buttonRef.current) return;
    function updatePosition() {
      const rect = buttonRef.current.getBoundingClientRect();
      const menuWidth = 224; // w-56 = 14rem = 224px
      let left = rect.right - menuWidth;
      // Keep within viewport
      if (left < 8) left = 8;
      if (left + menuWidth > window.innerWidth - 8) left = window.innerWidth - menuWidth - 8;
      setMenuPos({ top: rect.bottom + 8, left });
    }
    updatePosition();
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);
    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [open]);

  /* Close on outside click / Escape */
  useEffect(() => {
    if (!open) return;
    function handleClickOutside(e) {
      if (
        buttonRef.current && !buttonRef.current.contains(e.target) &&
        listRef.current && !listRef.current.contains(e.target)
      ) close();
    }
    function handleEscape(e) {
      if (e.key === "Escape") close();
    }
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [open, close]);

  return (
    <div className={className}>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="text-base">{current?.flag}</span>
        <span className="hidden sm:inline">{current?.name}</span>
        <ChevronDown size={16} className="opacity-70" />
      </button>

      {open
        ? createPortal(
            <ul
              ref={listRef}
              className="fixed w-56 max-h-[70vh] overflow-y-auto overscroll-contain rounded-xl border border-slate-200 bg-white shadow-xl"
              style={{ top: menuPos.top, left: menuPos.left, zIndex: 9999 }}
              role="listbox"
            >
              {languages.map((item) => (
                <li key={item.code}>
                  <button
                    type="button"
                    onClick={() => {
                      setLocale(item.code);
                      setOpen(false);
                    }}
                    className={`w-full px-3 py-2.5 text-left text-sm hover:bg-slate-50 flex items-center gap-2 ${
                      item.code === locale ? "bg-slate-100 font-medium" : ""
                    }`}
                    role="option"
                    aria-selected={item.code === locale}
                  >
                    <span className="text-base">{item.flag}</span>
                    <span>{item.name}</span>
                  </button>
                </li>
              ))}
            </ul>,
            document.body
          )
        : null}
    </div>
  );
}
